import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { 
  User, 
  Building2, 
  CreditCard, 
  Save, 
  CheckCircle, 
  XCircle, 
  Clock, 
  AlertCircle, 
  Loader2,
  Phone,
  Mail,
  MapPin,
  Calendar,
  Sliders,
  FileText,
  Shield,
  Eye,
  Lock,
  X,
  Check
} from 'lucide-react';
import { TIMEZONES, DEFAULT_TIMEZONE } from '../utils/timezones';
import { CURRENCIES, TAX_TYPES, DEFAULT_CURRENCY, DEFAULT_TAX_TYPE, DEFAULT_TAX_RATE } from '../utils/currencyTax';
import { useSettings } from '../contexts/SettingsContext';
import api from '../utils/api';
import WaiversTabClean from '../components/WaiversTabClean';
import { toast } from 'sonner';

const SettingsPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [processing, setProcessing] = useState(false);
  const { dateFormat, timeFormat, updateDateFormat, updateTimeFormat } = useSettings();
  
  // Organization data
  const [orgData, setOrgData] = useState({
    name: '',
    slug: '',
    ownerName: '',
    ownerEmail: '',
    ownerPhone: '',
    operationsPhone: '',  // Customer-facing contact
    operationsEmail: '',  // Customer-facing email
    city: '',
    timezone: DEFAULT_TIMEZONE,
    currency: DEFAULT_CURRENCY,
    taxType: DEFAULT_TAX_TYPE,
    taxRate: DEFAULT_TAX_RATE,
    country: 'USA',
    emergencyPhone: ''
  });
  
  // Waiver data
  const [waiverEnabled, setWaiverEnabled] = useState(false);
  const [waiverText, setWaiverText] = useState('');
  const [showWaiverPreview, setShowWaiverPreview] = useState(false);
  
  // Subscription data
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  
  // Account management modals
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [showChangeEmailModal, setShowChangeEmailModal] = useState(false);
  const [pendingEmailChange, setPendingEmailChange] = useState(null);

  useEffect(() => {
    fetchProfileData();
    checkPendingEmailChange();
    
    // Check if returning from Stripe checkout
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const paymentStatus = urlParams.get('payment');
    
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else if (paymentStatus === 'canceled') {
      toast.error('Payment was canceled');
      window.history.replaceState({}, '', '/settings');
    }
  }, []);
  
  const checkPendingEmailChange = async () => {
    try {
      const response = await api.get('/v2/auth/account/pending-email-change');
      if (response.data.hasPendingChange) {
        setPendingEmailChange({
          email: response.data.pendingEmail,
          requestedAt: response.data.requestedAt
        });
      }
    } catch (error) {
      console.error('Error checking pending email change:', error);
    }
  };

  const fetchProfileData = async () => {
    try {
      setLoading(true);
      
      // Fetch organization data
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      const orgId = userData.organizationId;
      
      if (orgId) {
        const orgResponse = await api.get(`/organizations/${orgId}`);
        const orgDataFromApi = orgResponse.data;
        setOrgData({
          ...orgDataFromApi,
          emergencyPhone: orgDataFromApi.emergencyPhoneForTickets || ''
        });
        
        // Save org currency to localStorage for use in booking wizard
        if (orgDataFromApi.currency) {
          localStorage.setItem('orgCurrency', orgDataFromApi.currency);
        }
        if (orgDataFromApi.timezone) {
          localStorage.setItem('orgTimezone', orgDataFromApi.timezone);
        }
      }
      
      // Load waiver settings from localStorage
      const waiversEnabled = localStorage.getItem('waiversEnabled') === 'true';
      const savedWaiver = localStorage.getItem('orgWaiverText') || '';
      const savedEmergency = localStorage.getItem('orgEmergencyPhone') || '';
      const savedTimezone = localStorage.getItem('orgTimezone') || DEFAULT_TIMEZONE;
      
      setWaiverEnabled(waiversEnabled);
      setWaiverText(savedWaiver);
      
      // Only override from localStorage if not already set from API
      setOrgData(prev => ({ 
        ...prev, 
        emergencyPhone: prev.emergencyPhone || savedEmergency,
        timezone: prev.timezone || savedTimezone
      }));
      
      // Fetch subscription status
      const subResponse = await api.get('/subscriptions/my-status');
      setSubscriptionStatus(subResponse.data);
      
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      
      const updateData = {
        name: orgData.name,
        ownerName: orgData.ownerName,
        ownerEmail: orgData.ownerEmail,
        ownerPhone: orgData.ownerPhone,
        operationsPhone: orgData.operationsPhone,  // Customer support number
        operationsEmail: orgData.operationsEmail,  // Customer support email
        city: orgData.city,
        timezone: orgData.timezone,
        country: orgData.country,
        emergencyPhoneForTickets: orgData.emergencyPhone
      };
      
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      const orgId = userData.organizationId;
      
      if (orgId) {
        await api.put(`/organizations/${orgId}`, updateData);
      }
      
      // Save to localStorage as well
      localStorage.setItem('organizationName', orgData.name);
      localStorage.setItem('orgTimezone', orgData.timezone);
      localStorage.setItem('orgEmergencyPhone', orgData.emergencyPhone);
      localStorage.setItem('orgCurrency', orgData.currency || 'USD');
      
      toast.success('Profile saved successfully!');
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveWaivers = () => {
    try {
      setSaving(true);
      localStorage.setItem('waiversEnabled', waiverEnabled);
      localStorage.setItem('orgWaiverText', waiverText);
      toast.success('Waiver settings saved!');
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error('Payment verification timed out. Please refresh the page.');
      setProcessing(false);
      return;
    }

    setProcessing(true);

    try {
      const response = await api.get(`/subscriptions/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        toast.success('🎉 Subscription activated! Welcome to BookingFlows!');
        fetchProfileData();
        setProcessing(false);
        window.history.replaceState({}, '', '/settings');
        return;
      } else if (response.data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        setProcessing(false);
        window.history.replaceState({}, '', '/settings');
        return;
      }

      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      toast.error('Error verifying payment. Please refresh the page.');
      setProcessing(false);
    }
  };

  const handleSubscribe = async () => {
    try {
      setProcessing(true);
      const originUrl = window.location.origin;
      
      const response = await api.post('/subscriptions/create-checkout', null, {
        params: {
          package_id: 'starter',
          origin_url: originUrl
        }
      });
      
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error('Failed to start checkout. Please try again.');
      setProcessing(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? You will lose access to premium features.')) {
      return;
    }

    try {
      setProcessing(true);
      await api.post('/subscriptions/cancel');
      toast.success('Subscription canceled');
      fetchProfileData();
    } catch (error) {
      console.error('Error canceling subscription:', error);
      toast.error('Failed to cancel subscription');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-500 text-white"><CheckCircle className="w-3 h-3 mr-1" /> Active</Badge>;
      case 'canceled':
        return <Badge className="bg-gray-500 text-white"><XCircle className="w-3 h-3 mr-1" /> Canceled</Badge>;
      case 'past_due':
        return <Badge className="bg-yellow-500 text-white"><AlertCircle className="w-3 h-3 mr-1" /> Past Due</Badge>;
      default:
        return <Badge className="bg-gray-400 text-white"><Clock className="w-3 h-3 mr-1" /> Inactive</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Profile & Settings</h1>
          <p className="text-gray-600 mt-2">Manage your organization, waivers, and preferences</p>
        </div>

        {/* Processing Overlay */}
        {processing && (
          <Card className="border border-blue-200 bg-blue-50 mb-6">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                <div>
                  <p className="font-medium text-blue-900">Processing payment...</p>
                  <p className="text-sm text-blue-700">Please wait while we verify your subscription</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="space-y-6">
            {/* Organization Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  Organization
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="orgName">Organization Name *</Label>
                    <Input
                      id="orgName"
                      value={orgData.name}
                      onChange={(e) => setOrgData({...orgData, name: e.target.value})}
                      placeholder="e.g., Austin Asset Tours"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="slug">Organization Slug</Label>
                    <Input
                      id="slug"
                      value={orgData.slug}
                      disabled
                      className="bg-gray-100"
                    />
                    <p className="text-xs text-gray-500">Cannot be changed after creation</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="city">Location</Label>
                    <div className="relative">
                      <MapPin className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
                      <Input
                        id="city"
                        value={orgData.city || ''}
                        onChange={(e) => setOrgData({...orgData, city: e.target.value})}
                        placeholder="e.g., Austin, TX"
                        className="pl-10"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="timezone">Timezone</Label>
                    <select
                      id="timezone"
                      value={orgData.timezone}
                      onChange={(e) => setOrgData({...orgData, timezone: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {TIMEZONES.map(tz => (
                        <option key={tz.value} value={tz.value}>{tz.label}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="currency">Currency</Label>
                    <select
                      id="currency"
                      value={orgData.currency || 'AED'}
                      onChange={(e) => setOrgData({...orgData, currency: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="AED">AED - UAE Dirham</option>
                      <option value="USD">USD ($) - US Dollar</option>
                    </select>
                    <p className="text-xs text-gray-500">Default currency for all services</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Customer Support Contact Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Phone className="w-5 h-5" />
                  Customer Support Contact
                </CardTitle>
                <p className="text-sm text-gray-500">
                  This info is shown to customers in booking confirmations, SMS, and tickets
                </p>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="operationsPhone">Support Phone Number</Label>
                    <div className="relative">
                      <Phone className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
                      <Input
                        id="operationsPhone"
                        type="tel"
                        value={orgData.operationsPhone || ''}
                        onChange={(e) => setOrgData({...orgData, operationsPhone: e.target.value.replace(/[^0-9+() -]/g, '')})}
                        placeholder="(512) 555-5555"
                        className="pl-10"
                      />
                    </div>
                    <p className="text-xs text-gray-500">
                      Customers will see this number for questions & logistics
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="operationsEmail">Support Email</Label>
                    <div className="relative">
                      <Mail className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
                      <Input
                        id="operationsEmail"
                        type="email"
                        value={orgData.operationsEmail || ''}
                        onChange={(e) => setOrgData({...orgData, operationsEmail: e.target.value})}
                        placeholder="support@yourcompany.com"
                        className="pl-10"
                      />
                    </div>
                    <p className="text-xs text-gray-500">
                      Optional - for customers who prefer email
                    </p>
                  </div>
                </div>
                
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    💡 <strong>Tip:</strong> Use a number that's monitored by your check-in staff or operations team. 
                    This keeps booking questions separate from your personal owner contact.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Owner Profile Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Owner Information
                </CardTitle>
                <p className="text-sm text-gray-500">
                  Your personal info (not shared with customers)
                </p>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="ownerName">Full Name *</Label>
                    <div className="relative">
                      <User className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
                      <Input
                        id="ownerName"
                        value={orgData.ownerName}
                        onChange={(e) => setOrgData({...orgData, ownerName: e.target.value})}
                        placeholder="Your full name"
                        className="pl-10"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="ownerEmail">Email Address *</Label>
                    <div className="relative">
                      <Mail className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
                      <Input
                        id="ownerEmail"
                        type="email"
                        value={orgData.ownerEmail}
                        disabled
                        className="pl-10 bg-gray-50"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowChangeEmailModal(true)}
                      className="text-sm text-blue-600 hover:text-blue-700 hover:underline"
                      data-testid="change-email-link"
                    >
                      Change email address
                    </button>
                    {pendingEmailChange && (
                      <div className="flex items-center gap-2 text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                        <Clock className="w-3 h-3" />
                        <span>Pending verification: {pendingEmailChange.email}</span>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="ownerPhone">Phone Number</Label>
                    <div className="relative">
                      <Phone className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
                      <Input
                        id="ownerPhone"
                        value={orgData.ownerPhone || ''}
                        onChange={(e) => setOrgData({...orgData, ownerPhone: e.target.value})}
                        placeholder="+1-555-0100"
                        className="pl-10"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Password</Label>
                    <div className="relative">
                      <Lock className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
                      <Input
                        type="password"
                        value="••••••••"
                        disabled
                        className="pl-10 bg-gray-50"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowChangePasswordModal(true)}
                      className="text-sm text-blue-600 hover:text-blue-700 hover:underline"
                      data-testid="change-password-link"
                    >
                      Change password
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Save Button */}
            <div className="flex justify-end">
              <Button
                onClick={handleSaveProfile}
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>

            {/* Billing Section */}
            <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Subscription & Billing
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Current Status */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm text-gray-600">Current Status</p>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-lg font-semibold capitalize">
                      {subscriptionStatus?.subscriptionPlan || 'Free'}
                    </span>
                    {getStatusBadge(subscriptionStatus?.subscriptionStatus)}
                  </div>
                </div>
                {subscriptionStatus?.subscriptionStartDate && (
                  <div className="text-right">
                    <p className="text-sm text-gray-600">Active Since</p>
                    <p className="text-sm font-medium">
                      {new Date(subscriptionStatus.subscriptionStartDate).toLocaleDateString()}
                    </p>
                  </div>
                )}
              </div>

              {/* Subscription Actions */}
              {subscriptionStatus?.subscriptionStatus !== 'active' ? (
                <div className="border border-blue-200 bg-blue-50 rounded-lg p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-bold text-blue-900 text-xl">Starter Plan</h3>
                      <p className="text-blue-700 mt-1">Get started with BookingFlows</p>
                      <div className="mt-3">
                        <span className="text-4xl font-bold text-blue-900">$0.99</span>
                        <span className="text-blue-700 text-lg">/month</span>
                      </div>
                      <ul className="mt-4 space-y-2">
                        <li className="flex items-center gap-2 text-sm text-blue-800">
                          <CheckCircle className="w-4 h-4 text-blue-600" />
                          Unlimited trip scheduling
                        </li>
                        <li className="flex items-center gap-2 text-sm text-blue-800">
                          <CheckCircle className="w-4 h-4 text-blue-600" />
                          Auto-scheduler access
                        </li>
                        <li className="flex items-center gap-2 text-sm text-blue-800">
                          <CheckCircle className="w-4 h-4 text-blue-600" />
                          Widget booking system
                        </li>
                        <li className="flex items-center gap-2 text-sm text-blue-800">
                          <CheckCircle className="w-4 h-4 text-blue-600" />
                          Multi-tenant support
                        </li>
                      </ul>
                    </div>
                  </div>
                  <Button
                    onClick={handleSubscribe}
                    disabled={processing}
                    className="w-full mt-6 bg-blue-600 hover:bg-blue-700"
                    size="lg"
                  >
                    {processing ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <CreditCard className="w-4 h-4 mr-2" />
                        Subscribe Now
                      </>
                    )}
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="border border-green-200 bg-green-50 rounded-lg p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <CheckCircle className="w-6 h-6 text-green-600" />
                      <h3 className="font-bold text-green-900 text-lg">Active Subscription</h3>
                    </div>
                    <p className="text-green-700">You&apos;re all set! Enjoying full access to BookingFlows.</p>
                    <p className="text-sm text-green-600 mt-2">
                      Billing: $0.99/month • Renews automatically
                    </p>
                  </div>

                  <Button
                    onClick={handleCancelSubscription}
                    disabled={processing}
                    variant="outline"
                    className="w-full border-red-200 text-red-600 hover:bg-red-50"
                  >
                    {processing ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <XCircle className="w-4 h-4 mr-2" />
                        Cancel Subscription
                      </>
                    )}
                  </Button>
                </div>
              )}

              {/* Test Mode Notice */}
              <div className="border-t pt-4">
                <div className="flex items-start gap-2 text-sm text-gray-600">
                  <AlertCircle className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                  <p>
                    <strong>Test Mode:</strong> This is a development subscription. 
                    Use test card: 4242 4242 4242 4242, any future date, any CVC.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Preferences Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sliders className="w-5 h-5" />
                Preferences
              </CardTitle>
              <p className="text-sm text-gray-600 mt-2">
                Customize your display settings and defaults
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Date Format */}
              <div className="space-y-3">
                <Label className="text-base font-semibold flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Date Format
                </Label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <button
                    onClick={() => updateDateFormat('MM/DD/YYYY')}
                    className={`p-4 border rounded-lg transition-colors ${
                      dateFormat === 'MM/DD/YYYY'
                        ? 'border-blue-600 bg-blue-50 text-blue-900'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="font-medium">MM/DD/YYYY</div>
                    <div className="text-sm text-gray-600 mt-1">10/17/2025</div>
                  </button>
                  <button
                    onClick={() => updateDateFormat('DD/MM/YYYY')}
                    className={`p-4 border rounded-lg transition-colors ${
                      dateFormat === 'DD/MM/YYYY'
                        ? 'border-blue-600 bg-blue-50 text-blue-900'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="font-medium">DD/MM/YYYY</div>
                    <div className="text-sm text-gray-600 mt-1">17/10/2025</div>
                  </button>
                  <button
                    onClick={() => updateDateFormat('YYYY-MM-DD')}
                    className={`p-4 border rounded-lg transition-colors ${
                      dateFormat === 'YYYY-MM-DD'
                        ? 'border-blue-600 bg-blue-50 text-blue-900'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="font-medium">YYYY-MM-DD</div>
                    <div className="text-sm text-gray-600 mt-1">2025-10-17</div>
                  </button>
                </div>
              </div>

              {/* Time Format */}
              <div className="space-y-3 pt-4 border-t">
                <Label className="text-base font-semibold flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Time Format
                </Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <button
                    onClick={() => updateTimeFormat('12h')}
                    className={`p-4 border rounded-lg transition-colors ${
                      timeFormat === '12h'
                        ? 'border-blue-600 bg-blue-50 text-blue-900'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="font-medium">12-hour</div>
                    <div className="text-sm text-gray-600 mt-1">11:00 PM</div>
                  </button>
                  <button
                    onClick={() => updateTimeFormat('24h')}
                    className={`p-4 border rounded-lg transition-colors ${
                      timeFormat === '24h'
                        ? 'border-blue-600 bg-blue-50 text-blue-900'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="font-medium">24-hour</div>
                    <div className="text-sm text-gray-600 mt-1">23:00</div>
                  </button>
                </div>
              </div>

              {/* Success Message */}
              <div className="pt-4 border-t">
                <div className="flex items-center gap-2 text-sm text-green-600">
                  <CheckCircle className="w-4 h-4" />
                  <span>Preferences are saved automatically</span>
                </div>
              </div>
            </CardContent>
          </Card>
        
          {/* Waivers Section */}
          <WaiversTabClean />
        </div>
      </div>
      
      {/* Change Password Modal */}
      {showChangePasswordModal && (
        <ChangePasswordModal 
          onClose={() => setShowChangePasswordModal(false)} 
        />
      )}
      
      {/* Change Email Modal */}
      {showChangeEmailModal && (
        <ChangeEmailModal 
          currentEmail={orgData.ownerEmail}
          onClose={() => setShowChangeEmailModal(false)}
          onSuccess={() => {
            setShowChangeEmailModal(false);
            checkPendingEmailChange();
          }}
        />
      )}
    </div>
  );
};

// ============================================================================
// CHANGE PASSWORD MODAL COMPONENT
// ============================================================================
const ChangePasswordModal = ({ onClose }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState(false);

  const isPasswordValid = 
    newPassword.length >= 8 &&
    /[a-z]/.test(newPassword) &&
    /[A-Z]/.test(newPassword) &&
    /[0-9]/.test(newPassword);

  const passwordsMatch = newPassword === confirmPassword && confirmPassword.length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!currentPassword) {
      toast.error('Please enter your current password');
      return;
    }
    
    if (!isPasswordValid) {
      toast.error('Please meet all password requirements');
      return;
    }
    
    if (!passwordsMatch) {
      toast.error('New passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await api.post('/v2/auth/account/change-password', {
        currentPassword,
        newPassword
      });
      
      toast.success('Password changed successfully!');
      onClose();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to change password';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Lock className="w-5 h-5 text-blue-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">Change Password</h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              data-testid="close-password-modal"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="currentPassword" className="text-sm font-medium text-gray-700">
              Current Password *
            </Label>
            <Input
              id="currentPassword"
              type={showPasswords ? "text" : "password"}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="Enter current password"
              disabled={loading}
              className="h-11"
              data-testid="current-password-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="newPassword" className="text-sm font-medium text-gray-700">
              New Password *
            </Label>
            <Input
              id="newPassword"
              type={showPasswords ? "text" : "password"}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
              disabled={loading}
              className="h-11"
              data-testid="new-password-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword" className="text-sm font-medium text-gray-700">
              Confirm New Password *
            </Label>
            <Input
              id="confirmPassword"
              type={showPasswords ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              disabled={loading}
              className="h-11"
              data-testid="confirm-password-input"
            />
            {confirmPassword && !passwordsMatch && (
              <p className="text-xs text-red-500">Passwords do not match</p>
            )}
            {passwordsMatch && (
              <p className="text-xs text-green-600 flex items-center gap-1">
                <Check className="w-3 h-3" /> Passwords match
              </p>
            )}
          </div>

          {/* Password Requirements */}
          <div className="bg-gray-50 rounded-lg p-3 space-y-1.5">
            <p className="text-xs font-semibold text-gray-700 mb-2">Password Requirements:</p>
            <RequirementCheck met={newPassword.length >= 8} text="8+ characters" />
            <RequirementCheck met={/[a-z]/.test(newPassword)} text="1 lowercase letter" />
            <RequirementCheck met={/[A-Z]/.test(newPassword)} text="1 uppercase letter" />
            <RequirementCheck met={/[0-9]/.test(newPassword)} text="1 number" />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="showPasswords"
              checked={showPasswords}
              onChange={() => setShowPasswords(!showPasswords)}
              className="rounded border-gray-300"
            />
            <label htmlFor="showPasswords" className="text-sm text-gray-600">
              Show passwords
            </label>
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || !isPasswordValid || !passwordsMatch || !currentPassword}
              className="flex-1 bg-blue-600 hover:bg-blue-700"
              data-testid="change-password-submit"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Changing...
                </span>
              ) : (
                "Change Password"
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ============================================================================
// CHANGE EMAIL MODAL COMPONENT
// ============================================================================
const ChangeEmailModal = ({ currentEmail, onClose, onSuccess }) => {
  const [password, setPassword] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail);
  const isDifferentEmail = newEmail.toLowerCase() !== currentEmail?.toLowerCase();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!password) {
      toast.error('Please enter your current password');
      return;
    }
    
    if (!isValidEmail) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    if (!isDifferentEmail) {
      toast.error('New email must be different from current email');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/v2/auth/account/request-email-change', {
        currentPassword: password,
        newEmail
      });
      
      toast.success(response.data.message || 'Verification email sent! Check your new inbox.');
      onSuccess();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to initiate email change';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Mail className="w-5 h-5 text-blue-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">Change Email</h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              data-testid="close-email-modal"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-800">
              <strong>How it works:</strong> We&apos;ll send a verification link to your new email. 
              Your email will only change after you click that link.
            </p>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium text-gray-700">
              Current Email
            </Label>
            <div className="px-3 py-2 bg-gray-100 rounded-lg text-gray-600 text-sm">
              {currentEmail}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="newEmail" className="text-sm font-medium text-gray-700">
              New Email Address *
            </Label>
            <Input
              id="newEmail"
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="Enter new email address"
              disabled={loading}
              className="h-11"
              data-testid="new-email-input"
            />
            {newEmail && !isValidEmail && (
              <p className="text-xs text-red-500">Please enter a valid email address</p>
            )}
            {newEmail && isValidEmail && !isDifferentEmail && (
              <p className="text-xs text-amber-600">New email must be different from current</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="emailPassword" className="text-sm font-medium text-gray-700">
              Current Password *
            </Label>
            <Input
              id="emailPassword"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password to confirm"
              disabled={loading}
              className="h-11"
              data-testid="email-change-password-input"
            />
            <p className="text-xs text-gray-500">Required to verify your identity</p>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-xs text-amber-800">
              <strong>Security notice:</strong> A notification will also be sent to your current 
              email ({currentEmail}) to alert you of this change request.
            </p>
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || !isValidEmail || !isDifferentEmail || !password}
              className="flex-1 bg-blue-600 hover:bg-blue-700"
              data-testid="change-email-submit"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Sending...
                </span>
              ) : (
                "Send Verification"
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ============================================================================
// HELPER COMPONENT
// ============================================================================
const RequirementCheck = ({ met, text }) => (
  <div className="flex items-center gap-2">
    {met ? <Check className="w-3 h-3 text-green-600" /> : <X className="w-3 h-3 text-red-500" />}
    <span className={`text-xs ${met ? 'text-green-700' : 'text-gray-600'}`}>{text}</span>
  </div>
);

export default SettingsPage;
